import React from 'react'
import { useTranslation } from 'react-i18next'
import { TaskDropdown } from '../TaskDropdown'
import { DurationDropdownContent } from '../DurationDropdown'
import { DeadlineDropdownContent } from '../DeadlineDropdown'
import { TaskDeadlineBadge } from '../TaskDeadlineBadge'
import { TaskDurationBadge } from '../TaskDurationBadge'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { timestampTooltip } from './timestampTooltip'
import type { Task } from '@/src/renderer/lib/db/models'
import type { DurationValue, TaskEntryData } from './TaskEntry.types'
import type { DeadlineValue } from '../DeadlineDropdown'
import type { CollapsedDropdowns } from './hooks/useCollapsedDropdowns'

interface CollapsedTimingsProps {
  task: TaskEntryData
  collapsedDeadlineValue: DeadlineValue | null
  isCollapsedDeadlineToday: boolean
  isCollapsedDeadlineTomorrow: boolean
  isCollapsedDeadlineOverdue: boolean
  locale: string
  /** Abbreviated deadline label for narrow layouts (list window mode). */
  compact?: boolean
  dropdowns: CollapsedDropdowns
  setDuration: (value: DurationValue | null) => void
  saveCollapsedToDb: (updates: Partial<Omit<Task, 'id'>>) => void
  selectOnDropdownOpen: (open: boolean) => void
  playSoftRemove: () => void
}

export const CollapsedTimings: React.FC<CollapsedTimingsProps> = ({
  task,
  collapsedDeadlineValue, isCollapsedDeadlineToday, isCollapsedDeadlineTomorrow, isCollapsedDeadlineOverdue,
  locale, compact = false,
  dropdowns,
  setDuration, saveCollapsedToDb, selectOnDropdownOpen,
  playSoftRemove,
}) => {
  const { t } = useTranslation()

  const markenTooltip = timestampTooltip(task, locale, t)

  return (
  <>
    {/* Ende vor Dauer: Ende folgt immer auf Start (Sven, 05.10.2026), wie im Aufgaben-Menü. */}
    {/* Deadline badge (collapsed) */}
    {collapsedDeadlineValue && (
      <div
        className={`task-entry__collapsed-badge task-entry__collapsed-badge--deadline${isCollapsedDeadlineOverdue ? ' task-entry__collapsed-badge--deadline-overdue' : isCollapsedDeadlineToday ? ' task-entry__collapsed-badge--deadline-today' : isCollapsedDeadlineTomorrow ? ' task-entry__collapsed-badge--deadline-tomorrow' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => dropdowns.switchTo('deadline')}
      >
        <TaskDropdown
          open={dropdowns.deadlineOpen}
          onOpenChange={(open) => { dropdowns.setDeadlineOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="end"
          alignOffset={16}
          dateSheet={{ title: task.title }}
          trigger={
            <button className="task-entry__collapsed-badge-trigger" type="button">
              <TaskDeadlineBadge value={collapsedDeadlineValue} locale={locale} compact={compact} />
            </button>
          }
        >
          <DeadlineDropdownContent
            value={collapsedDeadlineValue}
            onValueChange={(value) => {
              saveCollapsedToDb({
                dueDate: value?.date ?? null,
                dueTime: value?.time ?? null,
                duePeriod: value?.period ?? null,
              })
            }}
            onClose={() => dropdowns.setDeadlineOpen(false)}
            onRemove={() => { playSoftRemove(); saveCollapsedToDb({ dueDate: null, dueTime: null, duePeriod: null }); dropdowns.setDeadlineOpen(false) }}
            placement="badge"
          />
        </TaskDropdown>
      </div>
    )}

    {/* Duration badge (collapsed) */}
    {task.estimatedDuration && (
      <div
        className="task-entry__collapsed-badge"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => dropdowns.switchTo('duration')}
      >
        <TaskDropdown
          open={dropdowns.durationOpen}
          onOpenChange={(open) => { dropdowns.setDurationOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="end"
          alignOffset={16}
          trigger={
            /*
              **Die Uhrzeit des Abhakens liegt im Tooltip der Marke**
              (Nutzer-Vorgabe, 2026-09-18) — sie stand vorher als zweite Zahl
              rechts daneben, und „9s von 10m · 13:53" sind drei Zahlen für
              eine Zeile. Leerer Inhalt heißt „kein Tooltip": Eine offene
              Aufgabe hat keine Uhrzeit, und dann erscheint auch keiner.
            */
            <WithTooltip content={markenTooltip}>
              <button className="task-entry__collapsed-badge-trigger" type="button">
                <TaskDurationBadge
                  minutes={task.estimatedDuration}
                  mode={task.durationMode ?? 'duration'}
                  // Die gelaufene Zeit steht IN der Marke, vor der Dauer:
                  // „9s von 10m". Daneben wären es zwei Angaben ohne Bezug.
                  focusedSeconds={task.focusedSeconds}
                />
              </button>
            </WithTooltip>
          }
        >
          <DurationDropdownContent
            value={{ minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' }}
            onValueChange={(value) => {
              setDuration(value)
              saveCollapsedToDb({ estimatedDuration: value?.minutes ?? null, durationMode: value?.mode ?? null })
            }}
            onClose={() => dropdowns.setDurationOpen(false)}
            onRemove={() => { playSoftRemove(); saveCollapsedToDb({ estimatedDuration: null, durationMode: null }); dropdowns.setDurationOpen(false) }}
            placement="badge"
          />
        </TaskDropdown>
      </div>
    )}
  </>
  )
}
